// geographycontent5.js
// Section Five — Hydrology, Coasts and Glaciation
// Grade9Ready Geography content module
//
// Covers:
// 52 Drainage Basins
// 54 Rivers and Valleys
// 56 River Features of the Upper Stage
// 57 River Features of the Middle and Lower Stages
// 58 Flooding
// 62 The Power of the Sea
// 63 Coastal Landforms from Erosion
// 64 Coastal Landforms from Deposition
// 65 The Coast and People
// 66 Flood and Erosion Control
// 68 The Work of Ice
// 70 Glacial Erosion

export const geographyContent5 = {
  id: "geography-section-5",
  subject: "Geography",
  sectionNumber: 5,
  title: "Hydrology, Coasts and Glaciation",
  slug: "hydrology-coasts-and-glaciation",
  examBoardLink: "AQA GCSE Geography: physical processes, landscapes, hazards and geographical skills",
  intro:
    "This section explains how water, waves and ice shape landscapes. You will learn how rivers drain land, how valleys change from source to mouth, why floods happen, how coasts are eroded and protected, and how glaciers create distinctive upland landforms.",
  keySkills: [
    "Use geographical vocabulary accurately.",
    "Describe and explain physical processes in sequence.",
    "Interpret diagrams of river, coastal and glacial landforms.",
    "Explain how physical processes create specific landforms.",
    "Evaluate flood and coastal management strategies.",
    "Use examples to support extended answers."
  ],
  topics: [
    {
      id: "geo5-1-drainage-basins",
      title: "Drainage Basins",
      slug: "drainage-basins",
      pageRef: "Source booklet p.52",
      learningObjectives: [
        "Define a drainage basin.",
        "Identify the key parts of a river system.",
        "Explain how water moves through a drainage basin."
      ],
      keywords: [
        { term: "Drainage basin", definition: "The area of land drained by a river and its tributaries." },
        { term: "Watershed", definition: "The boundary between one drainage basin and another, often high land." },
        { term: "Source", definition: "The starting point of a river." },
        { term: "Mouth", definition: "Where a river enters the sea, lake or another larger body of water." },
        { term: "Tributary", definition: "A smaller river or stream that joins a larger river." },
        { term: "Confluence", definition: "The point where two rivers or streams meet." },
        { term: "Surface runoff", definition: "Water flowing over the land surface into rivers or drains." },
        { term: "Infiltration", definition: "Water soaking into the soil." },
        { term: "Throughflow", definition: "Water moving sideways through soil towards a river." },
        { term: "Groundwater flow", definition: "Water moving slowly through rock underground." }
      ],
      tutorial: [
        {
          heading: "What is a drainage basin?",
          body:
            "A drainage basin is a natural system. Rainfall enters the system as an input, moves through the basin by different flows, and eventually leaves through a river channel. Every river has its own drainage basin, although very large rivers may have many tributaries draining huge areas."
        },
        {
          heading: "The parts of a river system",
          body:
            "A river usually begins at a source in upland areas. Smaller streams and tributaries feed into the main channel. The watershed marks the edge of the drainage basin. The river then flows downstream until it reaches its mouth."
        },
        {
          heading: "How water moves",
          body:
            "When rain falls, some water is intercepted by vegetation, some infiltrates into the soil, some moves through the soil as throughflow, and some runs over the surface. Water that reaches the river increases discharge."
        }
      ],
      workedExamples: [
        {
          question: "Explain the difference between a tributary and a confluence.",
          answer:
            "A tributary is a smaller river or stream that joins a larger river. A confluence is the exact place where the tributary and the larger river meet."
        },
        {
          question: "Why do drainage basins often begin in upland areas?",
          answer:
            "Upland areas receive more rainfall because moist air is forced to rise, cool and condense. They also provide higher ground, so gravity allows streams to flow downhill into larger river channels."
        }
      ],
      commonMisconceptions: [
        "A watershed is not the same as a river bank; it is the boundary of the whole drainage basin.",
        "A drainage basin is not just the river channel; it includes all the surrounding land that drains into the river.",
        "A tributary joins a main river; it is not the mouth of the river."
      ],
      quickCheck: [
        {
          question: "What is the boundary between two drainage basins called?",
          options: ["Confluence", "Watershed", "Mouth", "Meander"],
          answer: "Watershed",
          explanation: "The watershed is the high land forming the edge of a drainage basin."
        },
        {
          question: "What is the point where two rivers meet called?",
          options: ["Source", "Mouth", "Confluence", "Estuary"],
          answer: "Confluence",
          explanation: "A confluence is where a tributary joins another river."
        },
        {
          question: "Which process means water soaking into soil?",
          options: ["Infiltration", "Erosion", "Abrasion", "Condensation"],
          answer: "Infiltration",
          explanation: "Infiltration is the movement of water from the surface into soil."
        }
      ],
      examPractice: [
        {
          question: "Describe the main features of a drainage basin.",
          marks: 4,
          indicativeContent:
            "A drainage basin is the area drained by a river and its tributaries. It has a source, tributaries, a main river channel, confluences, a mouth and a watershed boundary. Water enters through precipitation and reaches the river through runoff, throughflow and groundwater flow."
        }
      ],
      grade9Move:
        "When explaining drainage basins, treat them as systems: inputs, stores, transfers and outputs. This helps you move beyond description into process explanation."
    },

    {
      id: "geo5-2-rivers-and-valleys",
      title: "Rivers and Valleys",
      slug: "rivers-and-valleys",
      pageRef: "Source booklet p.54",
      learningObjectives: [
        "Explain how rivers erode, transport and deposit material.",
        "Describe how valleys change downstream.",
        "Link river processes to valley shape."
      ],
      keywords: [
        { term: "Erosion", definition: "The wearing away and removal of rock and sediment." },
        { term: "Transportation", definition: "The movement of material by a river." },
        { term: "Deposition", definition: "The dropping of sediment when a river loses energy." },
        { term: "Hydraulic action", definition: "Erosion caused by the force of water hitting the bed and banks." },
        { term: "Abrasion", definition: "Erosion caused by sediment scraping against the bed and banks." },
        { term: "Attrition", definition: "Sediment particles hitting each other and becoming smaller and rounder." },
        { term: "Solution", definition: "Dissolving of soluble minerals into river water." },
        { term: "Vertical erosion", definition: "Downward erosion that deepens the river channel." },
        { term: "Lateral erosion", definition: "Sideways erosion that widens the river channel or valley." }
      ],
      tutorial: [
        {
          heading: "River erosion",
          body:
            "Rivers erode by hydraulic action, abrasion, attrition and solution. In the upper course, rivers have more vertical erosion because they are flowing down steep gradients. This helps create narrow, steep-sided valleys."
        },
        {
          heading: "River transportation",
          body:
            "Rivers transport load by traction, saltation, suspension and solution. Large rocks may roll along the bed, smaller pebbles may bounce, fine sediment may be carried in suspension, and dissolved material may be carried in solution."
        },
        {
          heading: "River deposition",
          body:
            "Deposition happens when a river loses energy. This can occur when discharge falls, velocity slows, the gradient becomes gentler, or the river enters a lake or sea."
        }
      ],
      workedExamples: [
        {
          question: "Explain why valleys are often V-shaped in the upper course.",
          answer:
            "In the upper course, the river flows over a steep gradient and has high energy for vertical erosion. The river cuts down into the valley floor. Weathering loosens material on the valley sides, and mass movement moves it downslope, creating a narrow V-shaped valley."
        },
        {
          question: "How does river transportation differ from erosion?",
          answer:
            "Erosion wears away the bed and banks of the river. Transportation moves the eroded material downstream."
        }
      ],
      commonMisconceptions: [
        "Fast water does not always mean more deposition; deposition usually happens when energy decreases.",
        "Attrition does not erode the river bank directly; it wears down the river’s load.",
        "Vertical erosion deepens a channel; lateral erosion widens it."
      ],
      quickCheck: [
        {
          question: "Which type of erosion deepens a river channel?",
          options: ["Lateral erosion", "Vertical erosion", "Deposition", "Longshore drift"],
          answer: "Vertical erosion",
          explanation: "Vertical erosion cuts down into the river bed."
        },
        {
          question: "Which process makes river sediment smaller and rounder?",
          options: ["Attrition", "Infiltration", "Condensation", "Solution"],
          answer: "Attrition",
          explanation: "Attrition happens when stones collide with each other."
        }
      ],
      examPractice: [
        {
          question: "Explain how a river can change the shape of a valley.",
          marks: 6,
          indicativeContent:
            "In the upper course, vertical erosion deepens the channel and helps form a V-shaped valley. Weathering and mass movement move material from valley sides into the river. Further downstream, lateral erosion becomes more important, widening the valley and creating features such as meanders and floodplains."
        }
      ],
      grade9Move:
        "Use process chains: steep gradient → high vertical erosion → river cuts down → weathered material moves downslope → V-shaped valley forms."
    },

    {
      id: "geo5-3-upper-course-features",
      title: "River Features of the Upper Stage",
      slug: "upper-course-river-features",
      pageRef: "Source booklet p.56",
      learningObjectives: [
        "Describe upper-course river features.",
        "Explain the formation of waterfalls and gorges.",
        "Link upper-course features to gradient, discharge and erosion."
      ],
      keywords: [
        { term: "Upper course", definition: "The early stage of a river, usually near the source in upland areas." },
        { term: "Interlocking spurs", definition: "Ridges of land that a river winds around in its upper course." },
        { term: "Waterfall", definition: "A sudden drop in a river’s course, often caused by bands of hard and soft rock." },
        { term: "Gorge", definition: "A steep-sided narrow valley formed as a waterfall retreats upstream." },
        { term: "Hard rock", definition: "Rock that is more resistant to erosion." },
        { term: "Soft rock", definition: "Rock that is less resistant and erodes more quickly." }
      ],
      tutorial: [
        {
          heading: "Upper-course conditions",
          body:
            "The upper course is usually steep, narrow and shallow. Discharge is often lower than downstream, but the steep gradient gives the river energy for vertical erosion."
        },
        {
          heading: "Interlocking spurs",
          body:
            "In the upper course, the river often lacks the power to cut directly through hillsides. It winds around ridges of harder land, creating interlocking spurs."
        },
        {
          heading: "Waterfalls and gorges",
          body:
            "Waterfalls often form where hard rock lies above softer rock. The soft rock erodes faster, creating an overhang of hard rock. Eventually the overhang collapses, and the waterfall retreats upstream. Over time, this leaves a gorge."
        }
      ],
      workedExamples: [
        {
          question: "Explain how a waterfall forms.",
          answer:
            "A waterfall can form where a river flows over hard rock with softer rock below. The softer rock erodes faster by hydraulic action and abrasion, creating a step and then an overhang. The overhang collapses, and repeated collapse causes the waterfall to retreat upstream."
        }
      ],
      commonMisconceptions: [
        "Waterfalls do not stay fixed in one place forever; they often retreat upstream.",
        "A gorge is formed after repeated waterfall retreat, not before the waterfall forms.",
        "Interlocking spurs are common in the upper course, not the lower course."
      ],
      quickCheck: [
        {
          question: "Which feature is most associated with the upper course?",
          options: ["Levee", "Delta", "Interlocking spurs", "Estuary"],
          answer: "Interlocking spurs",
          explanation: "Interlocking spurs form where a young river winds around ridges of land."
        },
        {
          question: "What usually happens after a waterfall overhang collapses?",
          options: [
            "The waterfall retreats upstream",
            "The river stops flowing",
            "A beach forms",
            "The floodplain disappears"
          ],
          answer: "The waterfall retreats upstream",
          explanation: "Repeated collapse and erosion cause upstream retreat."
        }
      ],
      examPractice: [
        {
          question: "Explain the formation of a gorge.",
          marks: 4,
          indicativeContent:
            "A waterfall forms where softer rock erodes faster than harder rock. Undercutting creates an overhang, which collapses. The process repeats, causing the waterfall to retreat upstream and leaving a steep-sided gorge behind."
        }
      ],
      grade9Move:
        "Name the erosion processes involved, especially hydraulic action and abrasion, and explain collapse and retreat as a repeated sequence."
    },

    {
      id: "geo5-4-middle-lower-course-features",
      title: "River Features of the Middle and Lower Stages",
      slug: "middle-lower-course-river-features",
      pageRef: "Source booklet p.57",
      learningObjectives: [
        "Describe features of the middle and lower course.",
        "Explain the formation of meanders, oxbow lakes, floodplains and levees.",
        "Compare river processes at different stages."
      ],
      keywords: [
        { term: "Middle course", definition: "The stage of a river where the gradient becomes gentler and lateral erosion increases." },
        { term: "Lower course", definition: "The final stage of a river, usually near the mouth, with a wide channel and floodplain." },
        { term: "Meander", definition: "A bend in a river." },
        { term: "Oxbow lake", definition: "A cut-off meander forming a curved lake." },
        { term: "Floodplain", definition: "Flat land beside a river that is covered during floods." },
        { term: "Levee", definition: "A raised bank of deposited sediment beside a river channel." }
      ],
      tutorial: [
        {
          heading: "Middle-course features",
          body:
            "In the middle course, the river has more discharge and more lateral erosion. Meanders develop as faster water erodes the outside bend and slower water deposits material on the inside bend."
        },
        {
          heading: "Oxbow lakes",
          body:
            "Over time, erosion narrows the neck of a meander. During high flow, the river may cut through the neck, taking a straighter route. Deposition seals off the old bend, creating an oxbow lake."
        },
        {
          heading: "Lower-course features",
          body:
            "In the lower course, the river is wider and deeper, and the valley floor is broad and flat. Floodplains form through repeated flooding and deposition. Levees form when heavier material is deposited close to the channel during floods."
        }
      ],
      workedExamples: [
        {
          question: "Explain why erosion is strongest on the outside bend of a meander.",
          answer:
            "Water is deeper and faster on the outside bend, so it has more energy. This increases hydraulic action and abrasion, creating a river cliff."
        },
        {
          question: "Explain how levees form.",
          answer:
            "When a river floods, water spreads over the floodplain and loses energy. The heaviest sediment is deposited first, close to the river channel. Repeated floods build up raised banks called levees."
        }
      ],
      commonMisconceptions: [
        "A meander is not just any river curve; it forms because erosion and deposition work on opposite sides of the channel.",
        "Oxbow lakes form when meanders are cut off, not when a waterfall collapses.",
        "Levees are depositional features."
      ],
      quickCheck: [
        {
          question: "Where is erosion fastest on a meander?",
          options: ["Inside bend", "Outside bend", "Floodplain", "River mouth"],
          answer: "Outside bend",
          explanation: "The outside bend has deeper, faster water and more erosion."
        },
        {
          question: "What feature forms when a meander is cut off?",
          options: ["Waterfall", "Gorge", "Oxbow lake", "Spit"],
          answer: "Oxbow lake",
          explanation: "An oxbow lake is a former meander loop separated from the main channel."
        }
      ],
      examPractice: [
        {
          question: "Explain how a meander can develop into an oxbow lake.",
          marks: 6,
          indicativeContent:
            "Erosion on the outside bends narrows the neck of a meander. Deposition occurs on inside bends. During high discharge, the river may cut through the narrow neck and follow a straighter route. Deposition then seals off the old bend, leaving an oxbow lake."
        }
      ],
      grade9Move:
        "Use both erosion and deposition in your explanation. Many weaker answers mention only erosion."
    },

    {
      id: "geo5-5-flooding",
      title: "Flooding",
      slug: "flooding",
      pageRef: "Source booklet p.58",
      learningObjectives: [
        "Define flooding.",
        "Explain physical and human causes of flooding.",
        "Describe the impacts of flooding on people, the economy and the environment."
      ],
      keywords: [
        { term: "Flood", definition: "When water covers land that is normally dry." },
        { term: "Discharge", definition: "The volume of water flowing in a river channel, usually measured in cumecs." },
        { term: "Impermeable surface", definition: "A surface that water cannot easily soak through." },
        { term: "Saturated soil", definition: "Soil that is full of water and cannot absorb much more." },
        { term: "Urbanisation", definition: "The growth of towns and cities." },
        { term: "Deforestation", definition: "The removal of trees and woodland." }
      ],
      tutorial: [
        {
          heading: "What causes flooding?",
          body:
            "Flooding happens when a river has more water than its channel can hold. Physical causes include heavy rainfall, prolonged rainfall, saturated soil, snowmelt and steep slopes. Human causes include urbanisation, deforestation and building on floodplains."
        },
        {
          heading: "Why urban areas flood quickly",
          body:
            "Urban areas contain impermeable surfaces such as roads, roofs and pavements. These reduce infiltration and increase surface runoff. Drains can move water quickly into rivers, increasing discharge rapidly."
        },
        {
          heading: "Impacts of flooding",
          body:
            "Flooding can damage homes, destroy crops, disrupt transport, pollute water supplies and cause injury or death. However, some floods also deposit fertile silt on floodplains."
        }
      ],
      workedExamples: [
        {
          question: "Explain how deforestation can increase flood risk.",
          answer:
            "Trees intercept rainfall and their roots help water infiltrate into the soil. When trees are removed, less water is intercepted and more water reaches the ground quickly. This increases surface runoff and raises river discharge."
        },
        {
          question: "Explain why prolonged rainfall can cause flooding.",
          answer:
            "Long periods of rainfall can saturate the soil. Once the soil cannot absorb more water, additional rainfall flows over the surface into rivers, increasing discharge and flood risk."
        }
      ],
      commonMisconceptions: [
        "Floods are not only caused by heavy rainfall; land use, soil conditions and river management also matter.",
        "Urban drainage can increase flood risk by moving water quickly into channels.",
        "Floodplains are naturally likely to flood because they are beside rivers."
      ],
      quickCheck: [
        {
          question: "Which human activity can increase flood risk?",
          options: ["Afforestation", "Urbanisation", "Terracing", "Beach nourishment"],
          answer: "Urbanisation",
          explanation: "Urbanisation increases impermeable surfaces and surface runoff."
        },
        {
          question: "What happens when soil becomes saturated?",
          options: [
            "It absorbs unlimited water",
            "It cannot absorb much more water",
            "It becomes impermeable rock",
            "It stops runoff completely"
          ],
          answer: "It cannot absorb much more water",
          explanation: "Saturated soil is full of water, so runoff increases."
        }
      ],
      examPractice: [
        {
          question: "Explain two causes of river flooding.",
          marks: 4,
          indicativeContent:
            "Heavy or prolonged rainfall can saturate the ground, increasing surface runoff and river discharge. Urbanisation can also increase flooding because impermeable surfaces reduce infiltration and drains move water rapidly into river channels."
        },
        {
          question: "Assess the extent to which human activity increases flood risk.",
          marks: 8,
          indicativeContent:
            "Human activity can strongly increase flood risk through urbanisation, deforestation and building on floodplains. However, physical factors such as rainfall intensity, relief, geology and existing soil moisture also matter. A balanced answer should explain both before reaching a judgement."
        }
      ],
      grade9Move:
        "Separate physical causes from human causes, then link each cause to the river’s discharge."
    },

    {
      id: "geo5-6-power-of-the-sea",
      title: "The Power of the Sea",
      slug: "power-of-the-sea",
      pageRef: "Source booklet p.62",
      learningObjectives: [
        "Explain how waves shape the coastline.",
        "Distinguish between constructive and destructive waves.",
        "Describe the main processes of coastal erosion, transportation and deposition."
      ],
      keywords: [
        { term: "Swash", definition: "The movement of water up a beach after a wave breaks." },
        { term: "Backwash", definition: "The movement of water back down a beach towards the sea." },
        { term: "Constructive wave", definition: "A wave with stronger swash than backwash, usually building beaches." },
        { term: "Destructive wave", definition: "A wave with stronger backwash than swash, usually eroding beaches." },
        { term: "Longshore drift", definition: "The movement of sediment along a beach by angled swash and straight backwash." },
        { term: "Fetch", definition: "The distance wind blows over the sea before waves reach the coast." }
      ],
      tutorial: [
        {
          heading: "Wave energy",
          body:
            "Waves are created by wind blowing over the surface of the sea. The longer the fetch and the stronger the wind, the more energy waves can have. Powerful waves can erode cliffs, move sediment and reshape coastlines."
        },
        {
          heading: "Constructive and destructive waves",
          body:
            "Constructive waves have strong swash and weak backwash, so they tend to deposit material and build beaches. Destructive waves have weak swash and strong backwash, so they remove material from beaches and contribute to erosion."
        },
        {
          heading: "Longshore drift",
          body:
            "When waves approach the beach at an angle, swash carries sediment diagonally up the beach. Backwash then pulls it straight down under gravity. Repeated movement transports sediment along the coast."
        }
      ],
      workedExamples: [
        {
          question: "Explain how longshore drift moves sediment.",
          answer:
            "Waves approach the beach at an angle because of the prevailing wind. Swash carries sediment diagonally up the beach, while backwash pulls it straight back down. Repeated movement transports sediment along the coastline."
        }
      ],
      commonMisconceptions: [
        "Swash moves up the beach; backwash moves down the beach.",
        "Constructive waves build beaches; destructive waves usually erode them.",
        "Longshore drift moves material along the coast, not directly out to sea."
      ],
      quickCheck: [
        {
          question: "Which wave type usually has stronger backwash?",
          options: ["Constructive wave", "Destructive wave", "Tidal wave", "Standing wave"],
          answer: "Destructive wave",
          explanation: "Destructive waves have stronger backwash and tend to remove beach material."
        },
        {
          question: "What causes sediment to move along the coastline?",
          options: ["Longshore drift", "Attrition only", "Condensation", "Freeze-thaw weathering"],
          answer: "Longshore drift",
          explanation: "Longshore drift transports sediment along a beach."
        }
      ],
      examPractice: [
        {
          question: "Explain how waves can transport material along a beach.",
          marks: 4,
          indicativeContent:
            "Waves approach the coast at an angle due to the prevailing wind. Swash moves material diagonally up the beach. Backwash moves it straight down under gravity. Repetition of this process transports sediment along the coast by longshore drift."
        }
      ],
      grade9Move:
        "In coastal process answers, use the terms swash, backwash, prevailing wind and longshore drift accurately."
    },

    {
      id: "geo5-7-coastal-erosion-landforms",
      title: "Coastal Landforms from Erosion",
      slug: "coastal-landforms-from-erosion",
      pageRef: "Source booklet p.63",
      learningObjectives: [
        "Identify coastal landforms formed by erosion.",
        "Explain the formation of cliffs, wave-cut platforms, caves, arches, stacks and stumps.",
        "Use process sequencing in coastal landform explanations."
      ],
      keywords: [
        { term: "Headland", definition: "A section of harder rock that sticks out into the sea." },
        { term: "Bay", definition: "A curved inlet where softer rock has been eroded." },
        { term: "Cliff", definition: "A steep rock face along the coast." },
        { term: "Wave-cut notch", definition: "An undercut area at the base of a cliff caused by wave erosion." },
        { term: "Wave-cut platform", definition: "A flat rocky surface left behind as a cliff retreats." },
        { term: "Cave", definition: "A hollow in a cliff, usually formed by erosion along cracks or weaknesses." },
        { term: "Arch", definition: "A natural opening through a headland formed when caves erode through." },
        { term: "Stack", definition: "An isolated pillar of rock left after an arch collapses." },
        { term: "Stump", definition: "A short remnant of a stack after further erosion."
        }
      ],
      tutorial: [
        {
          heading: "Discordant coastlines",
          body:
            "Where bands of hard and soft rock meet the sea, softer rock is eroded more quickly. This can create bays, while harder rock remains as headlands."
        },
        {
          heading: "Cliffs and wave-cut platforms",
          body:
            "Waves attack the base of a cliff by hydraulic action and abrasion, creating a wave-cut notch. The cliff above becomes unsupported and collapses. Repeated collapse causes cliff retreat and leaves a wave-cut platform."
        },
        {
          heading: "Cave, arch, stack and stump sequence",
          body:
            "Cracks in a headland are enlarged by erosion to form caves. Caves may erode through the headland to create an arch. The arch roof eventually collapses, leaving a stack. Continued erosion reduces the stack to a stump."
        }
      ],
      workedExamples: [
        {
          question: "Explain how a stack forms.",
          answer:
            "Waves exploit weaknesses in a headland, forming caves. Continued erosion enlarges the caves until they break through to form an arch. Weathering and erosion weaken the arch roof, which collapses, leaving an isolated stack."
        },
        {
          question: "Explain how a wave-cut platform forms.",
          answer:
            "Waves erode the base of a cliff, creating a wave-cut notch. The overhanging cliff becomes unstable and collapses. As the process repeats, the cliff retreats and a flat rocky platform is left behind."
        }
      ],
      commonMisconceptions: [
        "Stacks form after arches collapse; they do not form before caves.",
        "Wave-cut platforms are exposed at low tide and show former cliff positions.",
        "Bays usually form in softer rock; headlands usually form in harder rock."
      ],
      quickCheck: [
        {
          question: "What feature forms after an arch collapses?",
          options: ["Bay", "Stack", "Spit", "Levee"],
          answer: "Stack",
          explanation: "A stack is an isolated pillar left after arch collapse."
        },
        {
          question: "What forms at the base of a cliff before collapse?",
          options: ["Wave-cut notch", "Oxbow lake", "Moraine", "Levee"],
          answer: "Wave-cut notch",
          explanation: "A wave-cut notch is created by erosion at the cliff base."
        }
      ],
      examPractice: [
        {
          question: "Explain the formation of a cave, arch, stack and stump.",
          marks: 6,
          indicativeContent:
            "Waves attack weaknesses in a headland using hydraulic action and abrasion. Cracks enlarge into caves. Continued erosion may cause caves to meet and form an arch. The arch roof is weakened by erosion and weathering and collapses, leaving a stack. Further erosion reduces the stack to a stump."
        }
      ],
      grade9Move:
        "Use the full landform sequence in the correct order and name the erosion processes involved."
    },

    {
      id: "geo5-8-coastal-deposition-landforms",
      title: "Coastal Landforms from Deposition",
      slug: "coastal-landforms-from-deposition",
      pageRef: "Source booklet p.64",
      learningObjectives: [
        "Explain how deposition creates coastal landforms.",
        "Describe the formation of beaches, spits and bars.",
        "Link deposition to changes in wave energy and sediment supply."
      ],
      keywords: [
        { term: "Beach", definition: "An accumulation of sand or shingle along the coast." },
        { term: "Spit", definition: "A narrow ridge of sand or shingle extending from the coast into the sea." },
        { term: "Bar", definition: "A ridge of deposited material that joins two areas of land across a bay." },
        { term: "Tombolo", definition: "A ridge of sediment connecting the mainland to an island." },
        { term: "Salt marsh", definition: "A sheltered coastal environment where fine sediment and vegetation accumulate." },
        { term: "Deposition", definition: "The dropping of sediment when energy decreases." }
      ],
      tutorial: [
        {
          heading: "Why deposition happens",
          body:
            "Deposition occurs when waves lose energy and can no longer carry sediment. This often happens in sheltered bays, behind spits, or where constructive waves build beaches."
        },
        {
          heading: "Beaches",
          body:
            "Beaches form when sediment is deposited between the high and low tide marks. Sandy beaches are often gently sloping, while shingle beaches are usually steeper."
        },
        {
          heading: "Spits",
          body:
            "Spits form when longshore drift transports sediment along the coast, but the coastline changes direction. Sediment continues to be deposited out into the sea or across an estuary. A hooked end may form due to changes in wind or wave direction."
        }
      ],
      workedExamples: [
        {
          question: "Explain how a spit forms.",
          answer:
            "Longshore drift transports sediment along the coast. Where the coastline changes direction, sediment continues to be moved and deposited out into the sea. Over time, a narrow ridge builds up. A hooked end may form if wind or wave direction changes."
        }
      ],
      commonMisconceptions: [
        "A spit is formed by deposition, not erosion.",
        "A bar crosses a bay; a spit is usually attached to land at one end.",
        "Deposition happens when wave energy decreases."
      ],
      quickCheck: [
        {
          question: "Which process is most important in spit formation?",
          options: ["Longshore drift", "Freeze-thaw weathering", "Hydraulic action only", "Plucking"],
          answer: "Longshore drift",
          explanation: "Longshore drift transports sediment along the coast before it is deposited."
        },
        {
          question: "What happens when waves lose energy?",
          options: ["They deposit sediment", "They always erode cliffs", "They form glaciers", "They increase river discharge"],
          answer: "They deposit sediment",
          explanation: "Deposition occurs when waves cannot carry their load."
        }
      ],
      examPractice: [
        {
          question: "Explain the formation of a spit.",
          marks: 4,
          indicativeContent:
            "Longshore drift moves sediment along the coast. Where the coastline changes direction, sediment continues to be transported and deposited into the sea. Over time, deposited material builds into a ridge attached to land at one end. A hooked end may develop if wind or wave direction changes."
        }
      ],
      grade9Move:
        "Mention both transport and deposition. Spits cannot be explained by deposition alone."
    },

    {
      id: "geo5-9-coast-and-people",
      title: "The Coast and People",
      slug: "the-coast-and-people",
      pageRef: "Source booklet p.65",
      learningObjectives: [
        "Explain why people use coastal areas.",
        "Describe the risks people face at the coast.",
        "Evaluate conflicts between different coastal users."
      ],
      keywords: [
        { term: "Coastal erosion", definition: "The wearing away of land by the sea." },
        { term: "Coastal flooding", definition: "Flooding caused by sea water covering normally dry coastal land." },
        { term: "Tourism", definition: "Travel for leisure, often important in coastal towns." },
        { term: "Conflict", definition: "Disagreement between groups with different needs or priorities." },
        { term: "Managed retreat", definition: "Allowing selected low-value coastal land to flood or erode naturally." }
      ],
      tutorial: [
        {
          heading: "Why coasts matter",
          body:
            "Coasts are important for tourism, fishing, ports, homes, wildlife habitats and industry. Many people choose to live and work near the coast because of access to trade, recreation and natural resources."
        },
        {
          heading: "Coastal risks",
          body:
            "Coastal communities can be threatened by erosion, flooding, storm surges and sea-level rise. The risk is greater where cliffs are made from soft rock, land is low-lying, or storms are frequent."
        },
        {
          heading: "Coastal conflict",
          body:
            "Different groups often want different things. Residents may want strong defences, tourists may want attractive beaches, environmental groups may want natural habitats protected, and councils must consider cost."
        }
      ],
      workedExamples: [
        {
          question: "Why can coastal management create conflict?",
          answer:
            "Coastal defences are expensive and may protect one area while increasing erosion elsewhere. Residents may want protection, but councils may not be able to justify the cost if only a small number of properties are at risk."
        }
      ],
      commonMisconceptions: [
        "Not every coastline can or should be defended.",
        "Coastal management decisions involve economic, social and environmental factors.",
        "Protecting one area can sometimes increase erosion further along the coast."
      ],
      quickCheck: [
        {
          question: "Which is a social reason to protect a coastline?",
          options: [
            "To protect homes and communities",
            "To increase hydraulic action",
            "To make waves destructive",
            "To stop all tides"
          ],
          answer: "To protect homes and communities",
          explanation: "Social impacts relate to people and communities."
        },
        {
          question: "Why might councils choose not to defend a coastline?",
          options: [
            "The cost may be greater than the value of what is protected",
            "Erosion is always useful",
            "Waves cannot be managed",
            "Hard engineering is free"
          ],
          answer: "The cost may be greater than the value of what is protected",
          explanation: "Cost-benefit decisions are central to coastal management."
        }
      ],
      examPractice: [
        {
          question: "Explain why coastal areas can create both opportunities and risks for people.",
          marks: 6,
          indicativeContent:
            "Coastal areas provide opportunities such as tourism, fishing, ports and employment. However, they also face risks from erosion, flooding and storms. These risks can damage homes, infrastructure and businesses. Management decisions can create conflict because protection is costly and may affect other parts of the coast."
        }
      ],
      grade9Move:
        "For evaluation questions, balance social, economic and environmental impacts before making a judgement."
    },

    {
      id: "geo5-10-flood-and-erosion-control",
      title: "Flood and Erosion Control",
      slug: "flood-and-erosion-control",
      pageRef: "Source booklet p.66",
      learningObjectives: [
        "Describe hard and soft engineering strategies.",
        "Explain how flood and coastal defences work.",
        "Evaluate the advantages and disadvantages of different management options."
      ],
      keywords: [
        { term: "Hard engineering", definition: "Building artificial structures to control natural processes." },
        { term: "Soft engineering", definition: "Working with natural processes to reduce risk." },
        { term: "Sea wall", definition: "A concrete wall built to reflect wave energy and protect the coast." },
        { term: "Groynes", definition: "Wooden or rock barriers that trap sediment moved by longshore drift." },
        { term: "Rock armour", definition: "Large rocks placed at the coast to absorb wave energy." },
        { term: "Beach nourishment", definition: "Adding sand or shingle to a beach to absorb wave energy." },
        { term: "Embankment", definition: "A raised bank built to reduce river flooding." },
        { term: "Floodplain zoning", definition: "Planning land use so high-risk flood areas are not heavily developed." }
      ],
      tutorial: [
        {
          heading: "Hard engineering",
          body:
            "Hard engineering includes sea walls, groynes, rock armour, flood walls and embankments. These can be effective but are often expensive and may affect natural processes."
        },
        {
          heading: "Soft engineering",
          body:
            "Soft engineering includes beach nourishment, dune regeneration, flood warnings, floodplain zoning and managed retreat. These strategies are usually more sustainable but may provide less immediate protection."
        },
        {
          heading: "Evaluating management",
          body:
            "Good evaluation considers effectiveness, cost, environmental impact, visual impact, maintenance and whether the strategy transfers risk elsewhere."
        }
      ],
      workedExamples: [
        {
          question: "Explain one advantage and one disadvantage of groynes.",
          answer:
            "Groynes trap sediment moved by longshore drift, helping to build a wider beach that absorbs wave energy. However, they can starve beaches further along the coast of sediment, increasing erosion elsewhere."
        },
        {
          question: "Why is floodplain zoning considered soft engineering?",
          answer:
            "It does not involve building large structures to control the river. Instead, it reduces risk by controlling where development happens."
        }
      ],
      commonMisconceptions: [
        "Hard engineering is not always better just because it is stronger.",
        "Soft engineering can be effective, but often needs space and long-term planning.",
        "Groynes may protect one beach while increasing erosion elsewhere."
      ],
      quickCheck: [
        {
          question: "Which is an example of soft engineering?",
          options: ["Sea wall", "Rock armour", "Beach nourishment", "Concrete flood wall"],
          answer: "Beach nourishment",
          explanation: "Beach nourishment works with natural beach processes."
        },
        {
          question: "What do groynes mainly do?",
          options: [
            "Trap sediment moved by longshore drift",
            "Create glaciers",
            "Increase river discharge",
            "Stop all erosion permanently"
          ],
          answer: "Trap sediment moved by longshore drift",
          explanation: "Groynes interrupt longshore drift and build beaches."
        }
      ],
      examPractice: [
        {
          question: "Evaluate the use of hard engineering to protect coastlines.",
          marks: 8,
          indicativeContent:
            "Hard engineering can be very effective at protecting property and infrastructure. Sea walls reflect wave energy, rock armour absorbs energy, and groynes build beaches. However, hard engineering is expensive, may look unnatural, needs maintenance and can increase erosion elsewhere. A strong judgement should consider whether the value of the protected land justifies the cost."
        },
        {
          question: "Compare hard and soft engineering approaches to flood management.",
          marks: 6,
          indicativeContent:
            "Hard engineering uses structures such as embankments, dams and flood walls to control water. It can provide strong protection but is expensive and may affect natural processes. Soft engineering includes floodplain zoning, warnings and wetland restoration. It is often more sustainable but may not prevent all flooding."
        }
      ],
      grade9Move:
        "In management questions, avoid saying one method is simply ‘best’. The best option depends on cost, risk, land value, environmental impact and timescale."
    },

    {
      id: "geo5-11-work-of-ice",
      title: "The Work of Ice",
      slug: "the-work-of-ice",
      pageRef: "Source booklet p.68",
      learningObjectives: [
        "Explain what glaciers are.",
        "Describe how ice moves.",
        "Identify the main processes by which glaciers shape landscapes."
      ],
      keywords: [
        { term: "Glacier", definition: "A large mass of ice that moves slowly downhill." },
        { term: "Accumulation", definition: "The build-up of snow and ice." },
        { term: "Ablation", definition: "Loss of ice through melting, evaporation or calving." },
        { term: "Plucking", definition: "Glacial erosion where ice freezes onto rock and pulls it away." },
        { term: "Abrasion", definition: "Glacial erosion where rocks embedded in ice scrape the valley floor and sides." },
        { term: "Freeze-thaw weathering", definition: "Weathering caused by water freezing, expanding and breaking rock apart." },
        { term: "Moraine", definition: "Material transported and deposited by a glacier." }
      ],
      tutorial: [
        {
          heading: "What is a glacier?",
          body:
            "A glacier is a large body of ice that moves under its own weight. Glaciers form where snow accumulates over many years and is compressed into ice."
        },
        {
          heading: "How glaciers move",
          body:
            "Glaciers move slowly downhill due to gravity. Movement can happen by internal deformation, where ice crystals shift, and by basal sliding, where meltwater helps the glacier slide over the ground."
        },
        {
          heading: "The work of ice",
          body:
            "Glaciers erode, transport and deposit material. They erode by plucking and abrasion, transport material within and on top of the ice, and deposit material as moraine when the ice melts."
        }
      ],
      workedExamples: [
        {
          question: "Explain the difference between plucking and abrasion.",
          answer:
            "Plucking happens when meltwater freezes onto rock and the glacier pulls pieces away as it moves. Abrasion happens when rocks embedded in the ice scrape and grind against the valley floor and sides."
        },
        {
          question: "Why do glaciers move downhill?",
          answer:
            "Glaciers move because gravity pulls the mass of ice downslope. Their movement is slow but powerful enough to erode and reshape valleys."
        }
      ],
      commonMisconceptions: [
        "Glaciers are not static; they move slowly downhill.",
        "Glacial abrasion is different from coastal abrasion, although both involve scraping.",
        "Glaciers can transport very large rocks because ice is powerful and slow-moving."
      ],
      quickCheck: [
        {
          question: "What is plucking?",
          options: [
            "Ice pulling rock away from the valley floor or sides",
            "Waves moving sediment along a beach",
            "A river depositing silt",
            "Wind forming sand dunes"
          ],
          answer: "Ice pulling rock away from the valley floor or sides",
          explanation: "Plucking is a glacial erosion process."
        },
        {
          question: "What is material deposited by a glacier called?",
          options: ["Moraine", "Levee", "Spit", "Delta"],
          answer: "Moraine",
          explanation: "Moraine is debris transported and deposited by glacial ice."
        }
      ],
      examPractice: [
        {
          question: "Explain how glaciers erode landscapes.",
          marks: 4,
          indicativeContent:
            "Glaciers erode by plucking and abrasion. In plucking, meltwater freezes onto rock and the glacier pulls pieces away as it moves. In abrasion, rocks embedded in the ice scrape and grind against the valley floor and sides, deepening and widening the valley."
        }
      ],
      grade9Move:
        "Do not just name plucking and abrasion. Explain exactly how each process works."
    },

    {
      id: "geo5-12-glacial-erosion",
      title: "Glacial Erosion",
      slug: "glacial-erosion",
      pageRef: "Source booklet p.70",
      learningObjectives: [
        "Describe glacial erosion landforms.",
        "Explain the formation of corries, arêtes, pyramidal peaks and U-shaped valleys.",
        "Use process-led explanations for glacial landforms."
      ],
      keywords: [
        { term: "Corrie", definition: "A bowl-shaped hollow on a mountainside formed by glacial erosion." },
        { term: "Arête", definition: "A sharp ridge between two corries." },
        { term: "Pyramidal peak", definition: "A sharp mountain peak formed where several corries erode back-to-back." },
        { term: "U-shaped valley", definition: "A wide, steep-sided valley formed by glacial erosion." },
        { term: "Truncated spur", definition: "A former interlocking spur cut off by a glacier." },
        { term: "Hanging valley", definition: "A smaller glacial valley left above the main valley floor." }
      ],
      tutorial: [
        {
          heading: "Corries",
          body:
            "Corries form where snow collects in a hollow on a mountainside. Over time, the snow compresses into ice. Plucking and abrasion deepen the hollow, while freeze-thaw weathering steepens the back wall. When the ice melts, a small lake called a tarn may remain."
        },
        {
          heading: "Arêtes and pyramidal peaks",
          body:
            "When two corries erode back-to-back, they create a narrow ridge called an arête. When three or more corries erode around a mountain, they can form a sharp pyramidal peak."
        },
        {
          heading: "U-shaped valleys",
          body:
            "Before glaciation, upland valleys are often V-shaped river valleys. A glacier fills the valley and erodes the floor and sides by plucking and abrasion. This creates a wider, deeper valley with steep sides and a flat floor."
        }
      ],
      workedExamples: [
        {
          question: "Explain how a corrie forms.",
          answer:
            "Snow collects in a hollow and is compressed into ice. The glacier moves in a circular motion, eroding by plucking and abrasion. Freeze-thaw weathering steepens the back wall. Over time, a deep bowl-shaped hollow forms, and after melting it may contain a tarn."
        },
        {
          question: "Explain how a U-shaped valley forms.",
          answer:
            "A glacier moves through a former river valley. Plucking and abrasion erode the valley floor and sides, making the valley wider and deeper. Interlocking spurs may be cut off, leaving truncated spurs and a steep-sided U-shaped valley."
        }
      ],
      commonMisconceptions: [
        "U-shaped valleys are formed by glaciers, not normal river erosion.",
        "Corries form high on mountainsides where snow accumulates.",
        "An arête is a ridge, not a peak. A pyramidal peak forms when several corries erode around a mountain."
      ],
      quickCheck: [
        {
          question: "What is a corrie?",
          options: [
            "A bowl-shaped hollow formed by glacial erosion",
            "A coastal depositional feature",
            "A river bend",
            "A ridge of sand across a bay"
          ],
          answer: "A bowl-shaped hollow formed by glacial erosion",
          explanation: "Corries are glacial erosion landforms found in upland areas."
        },
        {
          question: "What landform is created when two corries erode back-to-back?",
          options: ["Arête", "Spit", "Levee", "Oxbow lake"],
          answer: "Arête",
          explanation: "An arête is a sharp ridge between two corries."
        },
        {
          question: "Which process helps deepen and widen a glacial valley?",
          options: ["Plucking and abrasion", "Longshore drift", "Saltation only", "Evaporation"],
          answer: "Plucking and abrasion",
          explanation: "These are the main erosion processes of moving ice."
        }
      ],
      examPractice: [
        {
          question: "Explain the formation of a U-shaped valley.",
          marks: 6,
          indicativeContent:
            "A glacier moves downhill through a former V-shaped river valley. The ice erodes the valley floor and sides by plucking and abrasion. This makes the valley deeper, wider and straighter. Interlocking spurs may be cut off to form truncated spurs, leaving a steep-sided valley with a flat floor."
        },
        {
          question: "Explain the formation of a corrie.",
          marks: 6,
          indicativeContent:
            "Snow accumulates in a mountainside hollow and is compressed into ice. Rotational movement of the ice erodes the hollow by plucking and abrasion. Freeze-thaw weathering steepens the back wall. Over time the hollow deepens into a bowl shape, and after melting a tarn may form."
        }
      ],
      grade9Move:
        "For glacial landform answers, always link landform shape to the specific action of ice: plucking, abrasion, rotational movement and freeze-thaw weathering."
    }
  ],

  sectionReview: {
    title: "Section Five Review — Hydrology, Coasts and Glaciation",
    summary:
      "Water, waves and ice are powerful landscape-shaping forces. Rivers shape valleys through erosion, transportation and deposition. Coasts are changed by wave energy, erosion, longshore drift and deposition. Glaciers reshape upland areas through plucking, abrasion and freeze-thaw weathering. People try to manage flood and erosion risks, but decisions are often expensive and contested.",
    mustKnow: [
      "A drainage basin is the area drained by a river and its tributaries.",
      "Upper-course rivers mainly erode vertically, creating V-shaped valleys and waterfalls.",
      "Middle and lower-course rivers have more lateral erosion and deposition, forming meanders, floodplains and levees.",
      "Flood risk is affected by rainfall, relief, geology, soil saturation, urbanisation and deforestation.",
      "Constructive waves build beaches; destructive waves remove material.",
      "Longshore drift transports sediment along the coast.",
      "Coastal erosion forms cliffs, wave-cut platforms, caves, arches, stacks and stumps.",
      "Coastal deposition forms beaches, spits, bars and salt marshes.",
      "Hard engineering uses built structures; soft engineering works more with natural processes.",
      "Glaciers erode by plucking and abrasion.",
      "Glacial erosion forms corries, arêtes, pyramidal peaks and U-shaped valleys."
    ],
    highValueExamPhrases: [
      "This increases surface runoff and raises river discharge...",
      "The river loses energy, so deposition occurs...",
      "Hydraulic action and abrasion erode the base of the cliff...",
      "Repeated collapse causes the cliff to retreat inland...",
      "Longshore drift transports sediment along the coastline...",
      "Plucking and abrasion deepen and widen the valley...",
      "The most sustainable option depends on cost, environmental impact and the value of the land being protected..."
    ],
    mixedPractice: [
      {
        question: "Explain how physical and human factors can increase flood risk.",
        marks: 6,
        indicativeContent:
          "Physical factors include heavy rainfall, prolonged rainfall, saturated soil, steep slopes and impermeable rock. These increase runoff and river discharge. Human factors include urbanisation and deforestation. Urban surfaces reduce infiltration, while deforestation reduces interception. Both increase the speed and volume of water reaching the river."
      },
      {
        question: "Explain how coastal erosion can create a stack.",
        marks: 6,
        indicativeContent:
          "Waves exploit weaknesses in a headland through hydraulic action and abrasion. Cracks widen into caves. Continued erosion causes caves to break through and form an arch. The roof is weakened by erosion and weathering and eventually collapses, leaving an isolated stack."
      },
      {
        question: "Evaluate the use of hard engineering to manage coastal erosion.",
        marks: 8,
        indicativeContent:
          "Hard engineering can protect valuable property and infrastructure. Sea walls, rock armour and groynes can reduce erosion or build beaches. However, they are costly, may need maintenance, can look unnatural and may increase erosion elsewhere by interrupting sediment movement. A justified judgement should consider the value of the land, cost and long-term sustainability."
      },
      {
        question: "Explain how glacial erosion forms a U-shaped valley.",
        marks: 6,
        indicativeContent:
          "A glacier moves through a former river valley. Plucking removes blocks of rock and abrasion scrapes the valley floor and sides. This makes the valley deeper, wider and straighter. Interlocking spurs may be cut off to form truncated spurs, leaving a flat-bottomed, steep-sided U-shaped valley."
      }
    ]
  }
};

export default geographyContent5;